import store from '@/vuex/store'
import { createSettingHooksMixin } from '@/business-logic/setting-hooks/componentHooks'
import { createLazyHookSource, staticHookSource } from '@/business-logic/setting-hooks/settingHookSources'
import { getUserPreference } from '@/business-logic/user-preferences'
import { USER_PREFERENCE_REGISTRY } from '@/business-logic/user-preferences/userPreferenceRegistry'
import { getUserPreferenceUnderTeamPolicy } from '@/business-logic/team-advanced-settings/teamAdvancedSettingPolicies'

export { findVNodes, findComponentVNodes } from '@/business-logic/setting-hooks/componentHooks'

// The setting's value with its feature flag and plan gates applied, and the team policy once the team section is on.
export const readSettingValue = (key) => (
  store.getters['featureToggles/canUseTeamAdvancedSettings'] ? getUserPreferenceUnderTeamPolicy(key) : getUserPreference(key)
)

// A setting's hooks change nothing while it holds its default, so its code is only fetched once it leaves it.
export const isSettingChanged = (setting, value) => value !== undefined && value !== setting.default

// Each hook file is its own chunk, fetched on demand (webpack 'lazy' mode); only the file names ship in the app bundle.
export const createAppHookSource = () => {
  const hookFiles = require.context('../user-preferences/settings', true, /^\.\/[^/]+\/ext\/[^/]+\.js$/, 'lazy')
  return createLazyHookSource({
    paths: hookFiles.keys(), load: (path) => hookFiles(path), registry: USER_PREFERENCE_REGISTRY,
  })
}

// Watches each hooked setting and loads its hook files the first time its value is not the default.
export const loadChangedSettings = (source, readSetting, watch) => {
  source.settings.forEach((setting) => {
    watch(() => readSetting(setting.key), (value) => {
      if (isSettingChanged(setting, value)) source.loadSetting(setting.key)
    })
  })
}

const watchStore = (read, onChange) => store.watch(read, onChange, { immediate: true })

export default {
  install(Vue, {
    hooks, source, readSetting = readSettingValue, watch = watchStore,
  } = {}) {
    const hookSource = source || (hooks ? staticHookSource(hooks) : createAppHookSource())
    if (hookSource.lazy) loadChangedSettings(hookSource, readSetting, watch)
    Vue.mixin(createSettingHooksMixin(hookSource, readSetting))
  },
}
